/**
 * PajeTypeBadge — Phase 6a.16 per Spec §21.11.6.
 *
 * Color-coded badge for the 3 PAJE categories (Phase 6c.1a sealed):
 *
 *   • RECLASSIFICATION  → blue    (IFRS 16 transition opening reclasses,
 *                                   short-term / long-term lease liability
 *                                   reclassifications)
 *   • ADJUSTMENT        → violet  (year-end accruals, manual auditor-
 *                                   proposed corrections)
 *   • FX_REVALUATION    → orange  (closing-rate revaluation of FX-
 *                                   denominated lease balances)
 *
 * Visual chrome matches Phase 6a.19 StatusBadge for consistency: same
 * `inline-flex` pill, same Tailwind palette buckets (100/950-30 / 800-200
 * / 300-700), only the hue varies.
 */

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AggregatedPajeType } from "@/types";

export interface PajeTypeBadgeProps {
  type: AggregatedPajeType;
  className?: string;
}

const TYPE_CLASSES: Record<AggregatedPajeType, string> = {
  RECLASSIFICATION:
    "bg-blue-100 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700",
  ADJUSTMENT:
    "bg-violet-100 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200 border-violet-300 dark:border-violet-700",
  FX_REVALUATION:
    "bg-orange-100 dark:bg-orange-950/30 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700",
};

export function PajeTypeBadge({ type, className }: PajeTypeBadgeProps) {
  const { t } = useTranslation();
  return (
    <Badge
      variant="outline"
      className={cn(TYPE_CLASSES[type], className)}
      data-testid={`paje-type-badge-${type}`}
    >
      {t(`paje.type.${type}` as never) as string}
    </Badge>
  );
}
